import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  Loader2,
  Package,
  MessageSquare,
  RefreshCcw,
} from "lucide-react";
import { apiClient } from "../lib/api";
import {
  PRODUCT_ORDER,
  PRODUCT_SHORT_LABEL,
  PRODUCT_PACK_LABEL,
  PRODUCT_PRICE,
  PICKUP_CODE,
  PICKUP_COLOR,
  PICKUP_BADGE_CLASS,
  GRADE_DOT,
  formatAUD,
} from "../lib/products";

const MAX_POLL = 12;
const POLL_MS = 2000;
// Customer-facing SMS recipient — hardcoded to guarantee correct dialer target
// regardless of any deployment-env drift on OWNER_MOBILE_NUMBER.
const SMS_RECIPIENT = "+61426266655";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await apiClient.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid" && r.data.order) {
          setOrder(r.data.order);
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") {
          setStatus("error");
          return;
        }
        if (attempts >= MAX_POLL) {
          setStatus("pending");
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      } catch {
        if (attempts >= MAX_POLL) setStatus("error");
        else timer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  const paidTime = useMemo(() => {
    if (!order?.created_at) return "";
    try {
      return new Date(order.created_at).toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return order.created_at;
    }
  }, [order]);

  const qtyByProduct = useMemo(() => {
    if (!order) return {};
    return {
      superq_5w30_c3: order.qty_5w30_c3 || 0,
      superq_10w30: order.qty_10w30 || 0,
      superq_10w40: order.qty_10w40 || 0,
    };
  }, [order]);

  const smsHref = () => {
    if (!order) return "#";
    const q = {
      superq_5w30_c3: order.qty_5w30_c3 || 0,
      superq_10w30: order.qty_10w30 || 0,
      superq_10w40: order.qty_10w40 || 0,
    };
    const productLines = PRODUCT_ORDER
      .filter((pid) => q[pid] > 0)
      .map(
        (pid) =>
          `${PICKUP_CODE[pid]} / ${PICKUP_COLOR[pid]} — ${PRODUCT_SHORT_LABEL[pid]} — ${PRODUCT_PACK_LABEL[pid]} x ${q[pid]}`
      );
    const lines = [
      "Oil Order Paid",
      "",
      `Order ID: ${order.order_id}`,
      `Name: ${order.customer_name}`,
      `Business: ${order.business_name}`,
      `Mobile: ${order.mobile}`,
      "",
      "Products:",
      ...productLines,
      "",
      `Total Paid: $${Number(order.total).toFixed(2)}`,
      "Payment Status: PAID",
    ];
    const body = encodeURIComponent(lines.join("\n"));
    return `sms:${SMS_RECIPIENT}?&body=${body}`;
  };

  if (status === "checking") {
    return (
      <Center>
        <Loader2 className="h-8 w-8 animate-spin text-accent-app mb-4" />
        <div className="text-xl font-bold">Confirming payment…</div>
        <div className="text-muted-app text-sm mt-1">Verifying with Stripe.</div>
      </Center>
    );
  }
  if (status === "pending") {
    return (
      <Center>
        <div className="text-xl font-bold mb-1">Still processing</div>
        <p className="text-muted-app text-sm max-w-xs">
          Your payment is still being confirmed. Please refresh or show this screen to pickup staff.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="pressable mt-6 inline-flex items-center gap-2 bg-accent-app text-accent-fg rounded-full px-5 py-3 font-bold"
        >
          <RefreshCcw className="h-4 w-4" /> Refresh
        </button>
      </Center>
    );
  }
  if (status === "error" || !order) {
    return (
      <Center>
        <div className="text-xl font-bold text-red-500">Could not confirm</div>
        <p className="text-muted-app text-sm mt-1 max-w-xs">
          We could not confirm your payment. If money was taken, please contact the store.
        </p>
        <Link
          to="/tnb-order"
          className="pressable mt-6 bg-accent-app text-accent-fg rounded-full px-5 py-3 font-bold"
        >
          Back to order
        </Link>
      </Center>
    );
  }

  return (
    <div className="min-h-screen bg-app text-app pb-10">
      {/* Header brand */}
      <header className="border-b border-app">
        <div className="max-w-md mx-auto px-5 py-4">
          <div className="text-[17px] font-extrabold tracking-tight">
            <span className="text-accent-app">Quick Oil Order</span>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-5 pt-6">
        {/* Big success block */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 ring-4 ring-emerald-500/20 flex items-center justify-center">
            <CheckCircle2 className="h-9 w-9 text-emerald-400" strokeWidth={2.5} />
          </div>
          <h1 className="mt-4 text-[28px] font-extrabold tracking-tight leading-tight">
            Payment Successful
          </h1>
          <p className="mt-1.5 text-muted-app text-[14px]">
            Your order has been paid and confirmed.
          </p>
        </div>

        {/* Order ID + PAID */}
        <div className="mt-6 bg-elev border border-app rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-subtle-app font-bold">
            Order ID
          </div>
          <div className="mt-1 flex items-center justify-between gap-3">
            <div
              data-testid="success-order-id"
              className="font-mono tab-num text-[34px] font-extrabold text-accent-app tracking-tight leading-none"
            >
              {order.order_id}
            </div>
            <div className="inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-3 py-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="text-[12px] font-extrabold uppercase tracking-widest text-emerald-300">
                PAID
              </span>
            </div>
          </div>
        </div>

        {/* Payment details */}
        <div className="mt-4">
          <SectionTitle>Payment Details</SectionTitle>
          <div className="mt-3 bg-elev border border-app rounded-2xl p-5 space-y-3">
            <Row label="Payment Time" value={paidTime} />
            <Row
              label="Payment Status"
              value={
                <span className="text-emerald-400 font-bold uppercase tracking-wide text-[13px]">
                  Paid
                </span>
              }
            />
            <div className="border-t border-app pt-3 flex items-baseline justify-between">
              <span className="text-[13px] uppercase tracking-widest text-subtle-app font-bold">
                Total Paid
              </span>
              <span
                data-testid="success-total-paid"
                className="font-mono tab-num text-[24px] font-extrabold tracking-tight"
              >
                {formatAUD(order.total)}
              </span>
            </div>
            <div className="text-[11px] text-subtle-app text-right">
              GST included · {formatAUD(order.gst)}
            </div>
          </div>
        </div>

        {/* Ordered products */}
        <div className="mt-4">
          <SectionTitle>Ordered Products</SectionTitle>
          <div className="mt-3 bg-elev border border-app rounded-2xl divide-y divide-white/5 overflow-hidden">
            {PRODUCT_ORDER.map((pid) => {
              const q = qtyByProduct[pid] || 0;
              const line = q * PRODUCT_PRICE[pid];
              const dim = q === 0;
              return (
                <div
                  key={pid}
                  data-testid={`ordered-${pid}`}
                  className={`flex items-center justify-between gap-3 px-4 py-3 ${dim ? "opacity-40" : ""}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <span className={`mt-1.5 h-2 w-2 rounded-full ${GRADE_DOT[pid]}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center text-[10px] font-extrabold uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${PICKUP_BADGE_CLASS[pid]}`}
                        >
                          {PICKUP_CODE[pid]} / {PICKUP_COLOR[pid]}
                        </span>
                        <span className="text-[15px] font-semibold truncate">
                          {PRODUCT_SHORT_LABEL[pid]}
                        </span>
                      </div>
                      <div className="text-[12px] text-subtle-app mt-0.5">
                        {PRODUCT_PACK_LABEL[pid]} · × {q}
                      </div>
                    </div>
                  </div>
                  <div className="font-mono tab-num text-[15px] font-bold whitespace-nowrap">
                    {formatAUD(line)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Customer info */}
        <div className="mt-4">
          <SectionTitle>Customer Information</SectionTitle>
          <div className="mt-3 bg-elev border border-app rounded-2xl p-5 grid grid-cols-1 gap-3">
            <Row label="Customer Name" value={order.customer_name} />
            <Row label="Mobile Number" value={order.mobile} />
            <Row label="Email Address" value={order.email} mono truncate />
            <Row label="Business / Workshop" value={order.business_name} />
          </div>
        </div>

        {/* Pickup instruction */}
        <div className="mt-5 rounded-2xl border border-accent-app bg-accent-tint p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-accent-app/20 border border-accent-app flex items-center justify-center flex-shrink-0">
            <Package className="h-4 w-4 text-accent-app" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-accent-app font-bold">
              Pickup Instructions
            </div>
            <div className="text-[14px] mt-1 text-app">
              Please show this screen to pickup staff when collecting your order.
            </div>
          </div>
        </div>

        {/* Buttons */}
        <a
          href={smsHref()}
          data-testid="send-sms-button"
          className="pressable mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-app bg-elev hover:bg-elev-2 py-4 text-[14px] font-extrabold uppercase tracking-wider text-app"
        >
          <MessageSquare className="h-4 w-4" />
          Send Order Confirmation SMS
        </a>
        <button
          data-testid="place-another-order"
          onClick={() => navigate("/tnb-order")}
          className="pressable mt-3 w-full rounded-full bg-accent-app bg-accent-hover-app text-accent-fg py-4 text-[14px] font-extrabold uppercase tracking-wider"
        >
          Place Another Order
        </button>
      </div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div className="min-h-screen bg-app text-app flex flex-col items-center justify-center p-6 text-center">
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1" style={{ background: "rgb(var(--border))" }} />
      <h2 className="text-[10px] uppercase tracking-[0.25em] text-subtle-app font-bold">
        {children}
      </h2>
      <div className="h-px flex-1" style={{ background: "rgb(var(--border))" }} />
    </div>
  );
}

function Row({ label, value, mono, truncate }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-[12px] uppercase tracking-widest text-subtle-app font-semibold pt-0.5">
        {label}
      </div>
      <div
        className={`text-app text-[14px] font-medium text-right min-w-0 ${mono ? "font-mono tab-num" : ""} ${truncate ? "truncate max-w-[60%]" : ""}`}
        title={typeof value === "string" ? value : ""}
      >
        {value || "-"}
      </div>
    </div>
  );
}
